import { useState } from "react";
import {
  useCustom,
  useOne,
  useUpdate,
  useDelete,
  useCustomMutation,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { ActionBar, Button, CheckboxField, ConfirmDialog, Field, FormCard, SectionHeader, StatusBadge } from "@fawkes/ui";

import type { AdminUser } from "./UserList";
import { SetUserPasswordDialog } from "./SetUserPasswordDialog";
import { UserRolesSection } from "./UserRolesSection";
import { UserVestigingenSection } from "./UserVestigingenSection";
import { UserTeamsSection } from "./UserTeamsSection";

interface RoleCatalogEntry {
  id: string;
  name: string;
  description?: string;
}

/**
 * The reset link as something the recipient can actually paste into a browser.
 *
 * <p>The api builds `<base>/set-password#<token>` from `fawkes.api.password-reset.base-url`, which
 * defaults to blank — so an unconfigured deployment answers a bare `/set-password#…`. An admin hands
 * this link over out-of-band, where a path with no host is useless.
 *
 * <p>Resolving against the SPA's own origin is the right base by construction: this page IS served
 * from the host the user will visit. An absolute link from a configured api already wins, because
 * `new URL(absolute, base)` ignores the base it was given.
 */
function absoluteResetLink(link: string): string {
  try {
    return new URL(link, globalThis.location?.origin).toString();
  } catch {
    // A malformed link is still worth showing verbatim rather than swallowing it.
    return link;
  }
}

// Admin user detail + edit. Identity edit via useUpdate (PUT /api/users/{id}); soft-delete/restore +
// the password-reset-link action via useCustomMutation (relative urls — the dataProvider prepends /api).
// The reset link is revealed once after issuing (an admin hands it to the user out-of-band).
export function UserDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const { data, isLoading, isError } = useOne<AdminUser>({ resource: "users", id });
  const { mutate: update, isLoading: isSaving } = useUpdate();
  const { mutate: mutateDelete } = useDelete();
  const { mutate: mutateCustom } = useCustomMutation();
  const invalidate = useInvalidate();

  // The role catalog is the SAME for every user and depends on nothing this screen loads. Fetching it
  // here rather than inside the section takes it off the far side of the user read: it now flies in
  // parallel with it instead of waiting for it to resolve before the section even mounts. (Vestigingen
  // need no equivalent — their endpoint returns the still-addable list itself.)
  const { data: roleCatalogData, isError: roleCatalogError } = useCustom<RoleCatalogEntry[]>({
    url: "roles",
    method: "get",
  });

  // null (not []) while unknown — the section offers no controls over an unloaded read.
  const roleCatalog: RoleCatalogEntry[] | null = Array.isArray(roleCatalogData?.data)
    ? (roleCatalogData!.data as RoleCatalogEntry[])
    : null;

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [active, setActive] = useState(true);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = () => invalidate({ resource: "users", invalidates: ["detail"], id });

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !data?.data) {
    return <p className="fawkes-error">{t("userDetailView.loadFailed")}</p>;
  }
  const user = data.data;

  function startEdit() {
    setFirstName(user.firstName ?? "");
    setLastName(user.lastName ?? "");
    setEmail(user.email ?? "");
    setEmployeeId(user.employeeId ?? "");
    setActive(user.active);
    setEditing(true);
  }

  function save() {
    update(
      { resource: "users", id, values: { firstName, lastName, email, employeeId, active } },
      { onSuccess: () => setEditing(false) },
    );
  }

  function softDelete() {
    setActionError(null);
    mutateDelete(
      { resource: "users", id },
      { onSuccess: () => refresh(), onError: () => setActionError(t("userDetailView.action.failed")) },
    );
  }

  function restore() {
    setActionError(null);
    mutateCustom(
      { url: `users/${id}/restore`, method: "post", values: {} },
      { onSuccess: () => refresh(), onError: () => setActionError(t("userDetailView.action.failed")) },
    );
  }

  function issueResetLink() {
    setActionError(null);
    setResetLink(null);
    setCopied(false);
    mutateCustom(
      { url: `users/${id}/reset-link`, method: "post", values: {} },
      {
        onSuccess: (res) => setResetLink(absoluteResetLink((res.data as { link: string }).link)),
        onError: () => setActionError(t("userDetailView.resetLink.failed")),
      },
    );
  }

  function copyLink() {
    if (resetLink) {
      void globalThis.navigator?.clipboard?.writeText?.(resetLink);
      setCopied(true);
    }
  }

  return (
    <>
      <FormCard>
      <SectionHeader>{user.username}</SectionHeader>

      {editing ? (
        <>
          <Field label={t("userDetailView.field.firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Field label={t("userDetailView.field.lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <Field label={t("userDetailView.field.email")} type="email" invalidEmailMessage={t("validatie.emailadres")}
            value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label={t("userDetailView.field.employeeId")} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
          <CheckboxField label={t("userDetailView.field.active")} checked={active} onChange={(e) => setActive(e.target.checked)} />
          <ActionBar>
            <Button variant="primary" onClick={save} disabled={isSaving}>{t("common.save")}</Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>{t("common.cancel")}</Button>
          </ActionBar>
        </>
      ) : (
        <>
          <dl className="fawkes-keyvalue">
            <dt>{t("userDetailView.field.username")}</dt>
            <dd>{user.username}</dd>
            <dt>{t("userDetailView.field.name")}</dt>
            <dd>{[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}</dd>
            <dt>{t("userDetailView.field.email")}</dt>
            <dd>{user.email || "—"}</dd>
            <dt>{t("userDetailView.field.employeeId")}</dt>
            <dd>{user.employeeId || "—"}</dd>
            <dt>{t("userDetailView.field.status")}</dt>
            <dd>
              {user.removedAt ? (
                <StatusBadge tone="danger">{t("userListView.status.removed")}</StatusBadge>
              ) : (
                <StatusBadge tone={user.active ? "success" : "neutral"}>
                  {user.active ? t("userListView.status.active") : t("userListView.status.disabled")}
                </StatusBadge>
              )}
            </dd>
            <dt>{t("userDetailView.field.password")}</dt>
            <dd>
              {user.hasPassword ? t("userDetailView.password.set") : t("userDetailView.password.unset")}
              {user.mustChangePassword && (
                <StatusBadge tone="warning">{t("userDetailView.password.mustChange")}</StatusBadge>
              )}
            </dd>
          </dl>

          {actionError && <p className="fawkes-error" role="alert">{actionError}</p>}

          <ActionBar>
            {user.removedAt ? (
              <Button variant="secondary" onClick={restore}>{t("userDetailView.action.restore")}</Button>
            ) : (
              <Button
                variant="secondary"
                className="user-delete-button"
                onClick={() => setConfirmDelete(true)}
              >
                {t("userDetailView.action.delete")}
              </Button>
            )}
            <Button variant="secondary" onClick={onBack}>{t("common.back")}</Button>
            <Button variant="primary" onClick={startEdit}>{t("common.edit")}</Button>
            {!user.removedAt && (
              <Button variant="secondary" onClick={() => setPasswordOpen(true)}>
                {t("userDetailView.setPassword.button")}
              </Button>
            )}
            {!user.removedAt && (
              <Button variant="secondary" onClick={issueResetLink}>
                {t("userDetailView.resetLink.button")}
              </Button>
            )}
          </ActionBar>

          {/* Below the button that issues it — a secret that appears above its own trigger reads as
              something that was already there. Same callout the service-account client secret uses. */}
          {resetLink && (
            <div className="fawkes-secret-callout" role="status">
              <p className="fawkes-secret-callout__title">{t("userDetailView.resetLink.revealed")}</p>
              <code className="fawkes-code">{resetLink}</code>
              <Button variant="secondary" onClick={copyLink}>
                {copied ? t("userDetailView.resetLink.copied") : t("userDetailView.resetLink.copy")}
              </Button>
            </div>
          )}

          {passwordOpen && (
            <SetUserPasswordDialog
              userId={id}
              onClose={() => setPasswordOpen(false)}
              onDone={() => {
                setPasswordOpen(false);
                refresh();
              }}
            />
          )}
        </>
      )}
      </FormCard>

      {/*
        One FormCard per section — the app's multi-section detail pattern. It also keeps ActionBar's
        border-top acting as the footer rule of the identity card instead of a line drawn across the
        middle of the page, above content it has nothing to do with.

        Deliberately OUTSIDE the editing ternary, so the identity editor does not unmount them: an open
        add/confirm dialog would vanish mid-action, and their loaded state would be refetched for
        nothing every time an admin toggled "Bewerken".
      */}
      {!user.removedAt && (
        <>
          <FormCard>
            <UserRolesSection userId={user.id} catalog={roleCatalog} catalogError={roleCatalogError} />
          </FormCard>
          <FormCard>
            <UserVestigingenSection userId={user.id} />
          </FormCard>
          <FormCard>
            <UserTeamsSection userId={user.id} />
          </FormCard>
        </>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t("userDetailView.action.deleteTitle")}
          confirmLabel={t("userDetailView.action.delete")}
          cancelLabel={t("common.cancel")}
          danger
          onConfirm={() => {
            setConfirmDelete(false);
            softDelete();
          }}
          onCancel={() => setConfirmDelete(false)}
        >
          {t("userDetailView.action.deleteBody").replace("%s", user.username)}
        </ConfirmDialog>
      )}
    </>
  );
}
